import { authService } from '@/services/auth-service';
import { navigate } from '@/utils/navigate';
import { orgService } from '@/services/org-service';
import type { Org } from '@/types/org';
import { applyDevWatermark } from '@/services/dev-watermark';

class NuAppLanding extends HTMLElement {
  protected readonly root: ShadowRoot;

  private _orgs: Org[] = [];
  private _loading = true;
  private _loadError = '';
  private _orgEntitlements = new Map<string, string[]>();

  private _closeDropdown = () => {
    this.root.querySelector<HTMLElement>('#dropdown')?.style.setProperty('display', 'none');
  };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this._render();
    applyDevWatermark(this.root);
    this._loadOrgs();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._closeDropdown);
  }

  private async _loadOrgs() {
    try {
      this._orgs = await orgService.getOrgs();
      this._loading = false;
    } catch (err) {
      this._loading = false;
      this._loadError = err instanceof Error ? err.message : 'Failed to load organizations.';
    }
    this._render();
    if (this._orgs.length > 0) {
      this._loadOrgEntitlements();
    }
  }

  private async _loadOrgEntitlements() {
    const results = await Promise.allSettled(
      this._orgs.map(org => orgService.getOrgEntitlements(org.id))
    );
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        this._orgEntitlements.set(this._orgs[i].id, result.value);
      }
    });
    this._render();
  }

  private _render() {
    const isAuth = authService.isAuthenticated();
    const isNone = authService.getUserType() === 'NONE';
    const canCreateOrg = authService.hasEntitlement('ac/org/create');
    document.removeEventListener('click', this._closeDropdown);

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        *, *::before, *::after { box-sizing: border-box; }

        .page {
          min-height: 100vh;
          background: var(--paper, #E4DED2);
          color: var(--ink, #2A2622);
          font-family: 'Inter', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .top-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          border-bottom: 1px solid var(--line, #C9C0AC);
          background: rgba(228,222,210,0.92);
          backdrop-filter: blur(6px);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .brand {
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 18px;
          letter-spacing: -0.02em;
          display: flex;
          align-items: center;
          gap: 7px;
          text-decoration: none;
          color: var(--ink, #2A2622);
        }
        .dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--kiln, #C97A2E);
          display: inline-block;
          flex-shrink: 0;
        }

        .btn-login {
          display: inline-flex;
          align-items: center;
          padding: 5px 14px;
          background: var(--kiln, #C97A2E);
          color: var(--paper-light, #FAF7F0);
          border: none;
          border-radius: 5px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          transition: background .15s, transform .1s;
        }
        .btn-login:hover  { background: var(--kiln-dark, #A15F1F); }
        .btn-login:active { transform: translateY(1px); }

        .user-menu { position: relative; }
        .icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px; height: 34px;
          background: transparent;
          border: 1px solid var(--line, #C9C0AC);
          border-radius: 50%;
          cursor: pointer;
          color: var(--ink, #2A2622);
          transition: background .15s;
          padding: 0;
        }
        .icon-btn:hover { background: var(--paper-light, #FAF7F0); }

        .dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 180px;
          background: var(--paper-light, #FAF7F0);
          border: 1px solid var(--line, #C9C0AC);
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(42,38,34,.12);
          overflow: hidden;
          z-index: 20;
        }
        .dropdown-name {
          padding: 12px 16px 10px;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 13px;
          font-weight: 500;
          color: var(--ink, #2A2622);
          border-bottom: 1px solid var(--line, #C9C0AC);
        }
        .dropdown-item {
          display: block;
          width: 100%;
          padding: 10px 16px;
          background: transparent;
          border: none;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--ink-soft, #5A5346);
          text-align: left;
          cursor: pointer;
          transition: background .12s;
        }
        .dropdown-item:hover { background: var(--paper, #E4DED2); }

        .body {
          width: 100%;
          max-width: 480px;
          padding: 28px 20px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .body-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .section-label {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: var(--ink-soft, #5A5346);
        }
        .btn-new {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          background: var(--kiln, #C97A2E);
          color: var(--paper-light, #FAF7F0);
          border: none;
          border-radius: 5px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 12px;
          cursor: pointer;
          transition: background .15s, transform .1s;
        }
        .btn-new:hover  { background: var(--kiln-dark, #A15F1F); }
        .btn-new:active { transform: translateY(1px); }

        .body-msg {
          font-size: 15px;
          color: var(--ink-soft, #5A5346);
          text-align: center;
          padding: 48px 0;
        }
        .error-msg {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          color: #b94a2c;
          text-align: center;
          padding: 32px 0;
        }

        .org-card {
          background: var(--paper-light, #FAF7F0);
          border: 1px solid var(--line, #C9C0AC);
          border-radius: 6px;
          overflow: hidden;
        }
        .org-photo {
          height: 110px;
          background: var(--carbon, #6B5B95);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .org-initial {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 52px;
          font-weight: 700;
          color: rgba(250,247,240,0.55);
          line-height: 1;
        }
        .org-body {
          padding: 16px 18px 18px;
          border-bottom: 1px solid var(--line, #C9C0AC);
        }
        .org-name {
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 17px;
          margin-bottom: 4px;
        }
        .org-addr {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          color: var(--ink-soft, #5A5346);
          line-height: 1.6;
        }
        .org-actions {
          display: flex;
          gap: 10px;
          padding: 14px 18px;
        }
        .org-secondary {
          display: flex;
          gap: 10px;
          padding: 0 18px 14px;
        }
        .btn-primary {
          flex: 1;
          padding: 10px;
          background: var(--kiln, #C97A2E);
          color: var(--paper-light, #FAF7F0);
          border: none;
          border-radius: 5px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: background .15s, transform .1s;
        }
        .btn-primary:hover  { background: var(--kiln-dark, #A15F1F); }
        .btn-primary:active { transform: translateY(1px); }
        .btn-ghost {
          flex: 1;
          padding: 10px;
          background: transparent;
          border: 1px solid var(--line, #C9C0AC);
          border-radius: 5px;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--ink-soft, #5A5346);
          cursor: pointer;
          transition: background .15s;
        }
        .btn-ghost:hover { background: var(--paper, #E4DED2); }

        .spinner {
          width: 28px; height: 28px;
          border: 3px solid var(--line, #C9C0AC);
          border-top-color: var(--kiln, #C97A2E);
          border-radius: 50%;
          animation: spin .7s linear infinite;
          margin: 48px auto;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      </style>

      <div class="page">
        <header class="top-bar">
          <a class="brand" href="/"><span class="dot"></span>notify-u</a>
          ${isAuth ? `
            <div class="user-menu">
              <button class="icon-btn" id="user-btn" aria-label="User menu">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </button>
              <div class="dropdown" id="dropdown" style="display:none">
                <div class="dropdown-name">${authService.getDisplayName()}</div>
                <button class="dropdown-item" id="logout-btn">Log out</button>
              </div>
            </div>
          ` : `
            <button class="btn-login" id="login-btn">Log in</button>
          `}
        </header>
        <div class="body">
          ${isAuth ? isNone ? `
            <p class="body-msg">
              This app is available by invitation only.<br>
              <a href="/#start" style="color:var(--kiln,#C97A2E);">Contact us</a> to request access.
            </p>
          ` : `
            <div class="body-row">
              <span class="section-label">Your organizations</span>
              ${canCreateOrg ? `<button class="btn-new" id="new-org-btn">+ New</button>` : ''}
            </div>
            ${this._loading ? `<div class="spinner"></div>` : ''}
            ${this._loadError ? `<p class="error-msg">${this._loadError}</p>` : ''}
            ${!this._loading && !this._loadError && this._orgs.length === 0 ? `
              <p class="body-msg">No organizations yet.</p>
            ` : ''}
            ${this._orgs.map(org => this._orgCardTpl(org, this._orgEntitlements.get(org.id))).join('')}
          ` : `
            <p class="body-msg">Log in to continue.</p>
          `}
        </div>
      </div>
    `;

    if (isAuth) {
      document.addEventListener('click', this._closeDropdown);

      this.root.querySelector('#user-btn')!.addEventListener('click', (e) => {
        e.stopPropagation();
        const dd = this.root.querySelector<HTMLElement>('#dropdown')!;
        dd.style.display = dd.style.display !== 'none' ? 'none' : 'block';
      });

      this.root.querySelector('#dropdown')!.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      this.root.querySelector('#logout-btn')!.addEventListener('click', async () => {
        await authService.logout();
        this._render();
      });

      this.root.querySelector('#new-org-btn')?.addEventListener('click', () => {
        navigate('/app/org/new');
      });

      this._orgs.forEach(org => {
        this.root.querySelector(`#scan-${org.id}`)?.addEventListener('click', () => {
          navigate(`/app/org/${org.id}/scan`);
        });
        this.root.querySelector(`#edit-${org.id}`)?.addEventListener('click', () => {
          navigate(`/app/org/${org.id}`);
        });
        this.root.querySelector(`#logs-${org.id}`)?.addEventListener('click', () => {
          navigate(`/app/org/${org.id}/activity-logs`);
        });
        this.root.querySelector(`#tickets-${org.id}`)?.addEventListener('click', () => {
          navigate(`/app/org/${org.id}/tickets`);
        });
      });
    } else {
      this.root.querySelector('#login-btn')!.addEventListener('click', () => {
        navigate('/app/login');
      });
    }
  }

  private _orgCardTpl(org: Org, ents?: string[]): string {
    const initial = org.name.charAt(0).toUpperCase();
    const canScan = ents?.includes('ac/org/scan') ?? false;
    const canActivityLog = ents?.includes('ac/org/activity-log') ?? false;
    const canTickets = ents?.includes('ac/org/tickets') ?? false;
    const canManage = ents?.includes('ac/org/settings/manage') ?? false;
    const canManageAcl = ents?.includes('ac/org/acl/manage') ?? false;
    const hasSettings = canManage || canManageAcl;

    return `
      <div class="org-card">
        <div class="org-photo">
          <span class="org-initial">${initial}</span>
        </div>
        <div class="org-body">
          <div class="org-name">${org.name}</div>
          <div class="org-addr">${org.address.street1}<br>${org.address.city}, ${org.address.state}</div>
        </div>
        ${canScan ? `
        <div class="org-actions">
          <button class="btn-primary" id="scan-${org.id}">Scan</button>
        </div>` : ''}
        ${hasSettings || canActivityLog ? `
        <div class="org-secondary">
          ${hasSettings ? `<button class="btn-ghost" id="edit-${org.id}">Settings</button>` : ''}
          ${canActivityLog ? `<button class="btn-ghost" id="logs-${org.id}">Activity Logs</button>` : ''}
          ${canTickets ? `<button class="btn-ghost" id="tickets-${org.id}">Tickets</button>` : ''}
        </div>` : ''}
      </div>
    `;
  }
}

customElements.define('nu-app-landing', NuAppLanding);